import { useGate, useUnit } from 'effector-react';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import {
  Autocomplete,
  FormControl,
  FormHelperText,
  FormLabel,
  TextField,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { Listbox } from '../../components/listbox';
import { withControl } from '../../hoc/withControl';
import { useAutoSelectSingleOption } from '../../lib/use-auto-select-single-option';
import { ResidentObjectListControlGate, residentObjectListQuery } from './model';
import type { Object, ResidentObjectListData } from './types';

export const ResidentObjectListControl = withControl<ResidentObjectListData>(
  ({
    value,
    setValue,
    error,
    label,
    required,
    readOnly,
    disabled,
    isError,
    residentGuid = null,
    multiple = false,
    autoSelectSingleOption = false,
  }) => {
    useGate(ResidentObjectListControlGate, { residentGuid });

    const { data, pending } = useUnit(residentObjectListQuery);

    const theme = useTheme();

    const options = Array.isArray(data) ? data : [];

    const valueList = Array.isArray(value) ? value : [value].filter(Boolean);

    const selectedIds = valueList.map((item: Object | string) =>
      typeof item === 'object' ? item.id : item
    );

    const findedValue = options.filter(
      (item: Object) => selectedIds.indexOf(item.id) > -1
    );

    const selectedValue = multiple ? findedValue : (findedValue[0] ?? null);

    const setValueFromControl = useAutoSelectSingleOption({
      enabled: autoSelectSingleOption && Boolean(residentGuid),
      loading: pending,
      multiple,
      options,
      setValue,
      value,
    });

    const handleChange = (...args: [unknown, Object | Object[] | null]) => {
      const newValue = args[1];

      if (multiple) {
        const normalizedValue = Array.isArray(newValue) ? newValue : [];
        setValueFromControl(normalizedValue);
        return;
      }

      const normalizedValue = Array.isArray(newValue) ? (newValue[0] ?? null) : newValue;

      setValueFromControl(normalizedValue);
    };

    return (
      <FormControl required={required} disabled={disabled}>
        {label && (
          <FormLabel required={!readOnly && required} error={isError}>
            {label}
          </FormLabel>
        )}
        <Autocomplete
          multiple={multiple}
          disableCloseOnSelect={multiple}
          loading={pending}
          value={selectedValue}
          options={options}
          onChange={handleChange}
          slotProps={{
            listbox: {
              component: Listbox,
            },
          }}
          renderInput={(params): JSX.Element => (
            <TextField
              {...params}
              error={isError}
              slotProps={{
                input: {
                  ...params.InputProps,
                  readOnly,
                  endAdornment: readOnly ? null : (
                    <>
                      {pending && <CircularProgress size={20} color="inherit" />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
          renderOption={({ key, ...optionProps }, option, { inputValue }) => {
            const matches = match(option.label, inputValue, { insideWords: true });
            const parts = parse(option.label, matches);

            return (
              <li key={key} {...optionProps}>
                <div>
                  {parts.map(
                    (part: { text: string; highlight: boolean }, index: number) => (
                      <span
                        key={index}
                        style={{
                          color: part.highlight
                            ? theme.palette.primary.main
                            : theme.palette.text.primary,
                        }}
                      >
                        {part.text}
                      </span>
                    )
                  )}
                </div>
              </li>
            );
          }}
          readOnly={readOnly}
          disabled={disabled}
        />
        {error && <FormHelperText error>{error}</FormHelperText>}
      </FormControl>
    );
  }
);
